import { z } from 'zod';
import { ARRANGEMENT_GROUP_KINDS } from '../constants/arrangementGroup.constant';
import { objectIdSchema } from './common.schema';
import { arrangementSummarySchema, calendarDateSchema } from './arrangement.schema';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  THE ARRANGEMENT GROUP — manual, by the CP team (P3b.1).
 *
 *  ⚠️ PRESENTATIONAL AND ORGANISATIONAL ONLY.
 *
 *  A group changes NOTHING about fulfilment, allocation, duty generation or status. Each
 *  member is still filled by its own divisions and still completes on its own. There is no
 *  automatic condition: grouping is something a person decides, which is why there is no
 *  rule here that could ever create one.
 *
 *  That claim is FALSIFIABLE rather than promised: `arrangementGroup.test.ts` greps the
 *  service for `status`, `requirement_lines`, `allocations`, `Deployment` and `publish` and
 *  fails if any of them appears. A promise in a comment is not a guarantee.
 *
 *  ⚠️ DELIBERATELY NOT BUILT: whether a group prints as ONE COMBINED ORDER SHEET. That is
 *  the likeliest real reason to group and it is unanswered, along with the printed order
 *  format generally. The model is shaped so it can be added without reshaping: the group is
 *  a first-class document with its own id, its own name, its own order date and an ORDERED
 *  member array — so a later `GET /arrangement-groups/:id/order-sheet` reads exactly these
 *  columns. A `groupId` flag on the arrangement would have had none of those four.
 */

export const ARRANGEMENT_GROUP_LIMITS = {
  MEMBERS: 20,
} as const;

/**
 * ⚠️ `.min(2)` IS THE DESIGN, NOT A NICETY.
 *
 * A group of one is a row with a band round it. More to the point, it is what keeps the
 * EMPTY case unreachable: a unique multikey index on the member array indexes an empty
 * array as a single null key, so two empty groups would collide. With a floor of two here
 * and a removal that DELETES rather than empties, the case cannot arise.
 */
export const createArrangementGroupSchema = z.object({
  name: z.string().trim().min(1, 'Name this group').max(120),
  arrangementIds: z
    .array(objectIdSchema)
    .min(2, 'Choose at least two orders to group')
    .max(ARRANGEMENT_GROUP_LIMITS.MEMBERS),
});

export const updateArrangementGroupSchema = z
  .object({ name: z.string().trim().min(1).max(120).optional() })
  .refine((value) => Object.keys(value).length > 0, { message: 'Nothing to update' });

export const addGroupMemberSchema = z.object({
  arrangementId: objectIdSchema,
});

export const arrangementGroupSchema = z.object({
  id: objectIdSchema,
  name: z.string(),
  kind: z.enum(ARRANGEMENT_GROUP_KINDS),
  /** ⚠️ ALL MEMBERS SHARE ONE ORDER DATE. Taken from the first member, never typed. */
  orderDate: calendarDateSchema,
  members: z.array(arrangementSummarySchema),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

/** ⚠️ RE-EXPORTED, NOT REDEFINED — it lives in `arrangement.schema.ts` so the dependency
 *  between these two files runs one way only. See its header there. */
export { arrangementGroupRefSchema } from './arrangement.schema';
